import { FC, useState, useEffect } from 'react';
import { useAppSelector, useEnhancedTranslation } from '@app/hooks';
import './personal-account.scss';
import { NewButton } from '@app/components/NewButton/NewButton';
import {
  getGiftCardsData,
  getLastCardOperationsList,
  TCardOperationDateItem,
  TGiftCardsActiveCard,
  TGiftCardsArchiveCard,
} from '@app/api/giftCards/gift-cards.service';
import { formatDate, formatNumberWithSpaces } from '@app/utils';
import PersonalAccountValidCard from './PersonalAccountValidCard/PersonalAccountValidCard';
import PersonalAccountArchiveCard from './PersonalAccountArchiveCard/PersonalAccountArchiveCard';
import { limitOperationsData, mapOperationsData, TRecentOperationDate } from './helpers';
import PersonalAccountOperationsModal from './PersonalAccountOperationsModal/PersonalAccountOperationsModal';

export const PersonalAccount: FC = () => {
  const { t } = useEnhancedTranslation({ keyPrefix: 'account.personalAccount' });
  const [isArchiveVisible, setIsArchiveVisible] = useState(true);
  const [activeCardData, setActiveCardData] = useState<TGiftCardsActiveCard[]>([]);
  const [archiveCardData, setArchiveCardData] = useState<TGiftCardsArchiveCard[]>([]);
  const [recentOperations, setRecentOperations] = useState<TCardOperationDateItem[]>([]);
  const [currentLang, setCurrentLang] = useState('ru');
  const [mappedOperations, setMappedOperations] = useState<TRecentOperationDate[]>([]);
  const [isOperationsModalOpened, setIsOperationsModalOpened] = useState(false);

  function openOperationModal(): void {
    document.querySelector('body')?.classList.add('fixed');
    setIsOperationsModalOpened(true);
  }
  function closeOperationModal(): void {
    document.querySelector('body')?.classList.remove('fixed');
    setIsOperationsModalOpened(false);
  }

  const {
    authReducer: {
      access: { accessToken },
    },
  } = useAppSelector((state) => state);

  async function getData(token: string): Promise<void> {
    try {
      const res = await getGiftCardsData(token);
      setActiveCardData(res.active);
      setArchiveCardData(res.archive);
    } catch (error) {
      console.log(error);
    }
  }

  async function getOperationsData(token: string): Promise<void> {
    try {
      const res = await getLastCardOperationsList(token);
      setRecentOperations(res);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    getData(accessToken || '');
    getOperationsData(accessToken || '');
  }, [accessToken]);

  useEffect(() => {
    const lang = localStorage.getItem('lang');
    if (lang === 'ru' || lang === 'en') {
      setCurrentLang(lang);
    }
  }, []);

  useEffect(() => {
    setMappedOperations(limitOperationsData(mapOperationsData(recentOperations, currentLang)));
  }, [recentOperations, currentLang]);

  return (
    <div className="personal-account">
      <div className="personal-account__title">{t('title')}</div>
      <div className="personal-account__main-wrap">
        <div className="personal-account__info-wrap">
          <div className="personal-account__valid-cards-block">
            <div className="personal-account__subtitle">{t('validCards.title')}</div>
            <div className="personal-account__valid-cards-wrap">
              {activeCardData.map((item) => (
                <PersonalAccountValidCard
                  key={item?.cardId}
                  date={formatDate(new Date(item.expireDate))}
                  image={`${process.env.REACT_APP_IMAGES_URL}/${item.product.imageUrl}`}
                  nominal={item.nominal}
                  remains={item.availAmnt}
                  giftMessage={item.giftMessage}
                  cardNumber={item.cardNumber}
                  title={item.product.name[currentLang] || ''}
                />
              ))}
            </div>
          </div>
          <div className="personal-account__archive-cards-block">
            <button
              type="button"
              className={`personal-account__archiveTitle ${
                isArchiveVisible ? 'personal-account__archiveTitle--active' : ''
              }`}
              onClick={() => setIsArchiveVisible((prev) => !prev)}
            >
              <div className="personal-account__subtitle">{t('archive.title')}</div>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path
                  d="M16 10.5402L4 22.5402L4.12632 22.6665C4.12632 22.6665 10.7263 16.0981 16 16.0981C21.2737 16.0981 27.8737 22.6665 27.8737 22.6665L28 22.5402L16 10.5402Z"
                  fill="#1F1F1F"
                />
              </svg>
            </button>
            <div
              className={`personal-account__archive-list ${
                isArchiveVisible ? 'personal-account__archive-list--visible' : ''
              }`}
            >
              {archiveCardData.map((item) => (
                <PersonalAccountArchiveCard
                  key={item?.cardId}
                  title={item.product.name[currentLang] || ''}
                  date={formatDate(new Date(item.expireDate))}
                  image={`${process.env.REACT_APP_IMAGES_URL}/${item.product.imageUrl}`}
                  nominal={item.nominal}
                  giftMessage={item.giftMessage}
                  cardNumber={item.cardNumber}
                  availAmnt={item.availAmnt}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="personal-account__operations-block">
          <div className="personal-account__subtitle">{t('operations.title')}</div>
          <div className="personal-account__operations-list">
            {mappedOperations?.map((item) => {
              return (
                <div className="personal-account__operations-list-block" key={`${item.date}-block`}>
                  <div className="personal-account__operations-list-block-title">{item.date}</div>
                  {item.items.map((operation, opIndex) => {
                    return (
                      <div className="personal-account__operation" key={`${`${operation.type}-${opIndex}`}key`}>
                        <div className="personal-account__operation-info-wrap">
                          <div className="personal-account__operation-img">
                            {operation?.image ? (
                              <img src={`${process.env.REACT_APP_IMAGES_URL}${operation.image}`} alt="" />
                            ) : (
                              <img src="/img/shop_plug.png" alt="" />
                            )}
                          </div>
                          <div className="personal-account__operation-info-block">
                            <div className="personal-account__operation-info-title">{operation.cardName}</div>
                            <div className="personal-account__operation-info-name">
                              {t(`operations.${operation.caption}`)}
                            </div>
                          </div>
                        </div>
                        {operation.value && operation.type !== 'return' && operation.type !== 'refund' && (
                          <div className="personal-account__operation-cost">
                            {operation.type === 'activation' ? '+' : '-'} {formatNumberWithSpaces(operation.value)} ₽
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <NewButton size="small" addClass="personal-account__operations-btn" onClick={() => openOperationModal()}>
            {t('operations.btn')}
          </NewButton>
        </div>
      </div>
      {isOperationsModalOpened && (
        <PersonalAccountOperationsModal
          onClose={() => closeOperationModal()}
          activeCardData={activeCardData}
          archiveCardData={archiveCardData}
        />
      )}
    </div>
  );
};
